import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  PencilLine,
  Trash2,
  KeyRound,
  Search,
  X,
  PlusCircle,
  Users
} from 'lucide-react';
import AddUserModal from './AddUserModal';
import ChangePasswordModal from './ChangePasswordModal';
import ConfirmDialog from '../../Dialogs/ConfirmDialog';
import { authservice } from '../../../../components/api/authservice';
import { useAccessTheme } from './useAccessTheme';

interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  tenantId: string;
  roleID: string;
  roleName?: string;
}

interface UserManagementModalProps {
  onClose: () => void;
}

const UserManagementModal: React.FC<UserManagementModalProps> = ({ onClose }) => {
  const t = useAccessTheme();
  const [search, setSearch] = useState('');
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedUserForPassword, setSelectedUserForPassword] = useState<User | null>(null);
  const [userToDelete, setUserToDelete] = useState<User | null>(null);
  const [editUserId, setEditUserId] = useState<string | null>(null);
  const [showUserModal, setShowUserModal] = useState(false);

  const refreshUserList = async () => {
    setLoading(true);
    try {
      const res = await authservice.getUsers();
      setUsers(res || []);
    } catch (err) {
      console.error('Failed to load users:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refreshUserList();
  }, []);

  const handleDeleteUser = async () => {
    if (!userToDelete) return;
    try {
      await authservice.deleteUser(userToDelete.id, userToDelete.tenantId);
      setUsers((prev) => prev.filter((u) => u.id !== userToDelete.id));
    } catch (err) {
      console.error('Failed to delete user:', err);
    } finally {
      setUserToDelete(null);
    }
  };

  const handlePasswordUpdate = async (newPassword: string) => {
    if (!selectedUserForPassword) return;
    try {
      await authservice.updatePassword(selectedUserForPassword.id, newPassword, selectedUserForPassword.tenantId);
    } catch (err) {
      console.error('Failed to update password:', err);
    } finally {
      setSelectedUserForPassword(null);
    }
  };

  const filteredUsers = users.filter((user) =>
    user.email.toLowerCase().includes(search.toLowerCase()) ||
    `${user.firstName} ${user.lastName}`.toLowerCase().includes(search.toLowerCase())
  );

  return createPortal(
    <div className={`fixed inset-0 z-[100] flex items-center justify-center px-4 py-6 ${t.overlay}`}>
      <div className={`rounded-xl shadow-xl w-full max-w-4xl overflow-hidden border ${t.panel}`}>
        <div className={`flex items-center justify-between px-5 py-4 border-b ${t.header}`}>
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${t.iconBg}`}>
              <Users className="w-5 h-5" />
            </div>
            <div>
              <h3 className={`text-lg font-semibold ${t.title}`}>Users Management</h3>
              <p className={`text-xs ${t.muted}`}>Add, edit, or remove system users.</p>
            </div>
          </div>
          <button type="button" onClick={onClose} className={`p-2 rounded-lg ${t.closeBtn}`}>
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5">
          <div className="flex flex-col md:flex-row justify-between gap-3 mb-4">
            <button
              type="button"
              onClick={() => { setEditUserId(null); setShowUserModal(true); }}
              className={`flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium ${t.btnPrimary}`}
            >
              <PlusCircle className="w-4 h-4" /> Add New User
            </button>
            <div className="relative flex-1 max-w-md">
              <Search className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${t.muted}`} />
              <input
                type="text"
                placeholder="Search by name or email..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className={`w-full border rounded-lg pl-9 pr-3 py-2 text-sm outline-none ${t.input}`}
              />
            </div>
          </div>

          <div className={`overflow-hidden border rounded-lg ${t.tableShell}`}>
            <div className="overflow-x-auto max-h-[50vh]">
              <table className="w-full text-left text-sm">
                <thead className={`${t.tableHead} text-xs uppercase tracking-wide`}>
                  <tr>
                    <th className="px-4 py-3">User</th>
                    <th className="px-4 py-3">Role</th>
                    <th className="px-4 py-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={3} className={`px-4 py-12 text-center ${t.muted}`}>Loading users...</td>
                    </tr>
                  ) : filteredUsers.length > 0 ? (
                    filteredUsers.map((user) => (
                      <tr key={user.id} className={`border-t ${t.rowBorder} ${t.rowHover}`}>
                        <td className="px-4 py-3">
                          <div className={`font-medium ${t.title}`}>{user.firstName} {user.lastName}</div>
                          <div className={`text-xs ${t.muted}`}>{user.email}</div>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex px-2 py-0.5 rounded text-xs border ${t.badge}`}>
                            {user.roleName ?? user.roleID}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex justify-end gap-1">
                            <button type="button" onClick={() => { setEditUserId(user.id); setShowUserModal(true); }}
                              className={`p-1.5 rounded ${t.muted} ${t.actionHover} hover:text-indigo-500`} title="Edit">
                              <PencilLine className="w-4 h-4" />
                            </button>
                            <button type="button" onClick={() => setSelectedUserForPassword(user)}
                              className={`p-1.5 rounded ${t.muted} ${t.actionHover} hover:text-amber-500`} title="Change Password">
                              <KeyRound className="w-4 h-4" />
                            </button>
                            <button type="button" onClick={() => setUserToDelete(user)}
                              className={`p-1.5 rounded ${t.muted} ${t.actionHover} hover:text-red-500`} title="Delete">
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={3} className={`text-center py-12 ${t.muted}`}>No users found.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div className={`px-5 py-4 border-t flex justify-end ${t.footer}`}>
          <button type="button" onClick={onClose} className={`px-4 py-2 rounded-lg text-sm font-medium ${t.btnSecondary}`}>
            Close
          </button>
        </div>
      </div>

      {showUserModal && (
        <AddUserModal
          onClose={() => setShowUserModal(false)}
          onSubmit={() => { setShowUserModal(false); refreshUserList(); }}
          userId={editUserId || undefined}
        />
      )}
      {selectedUserForPassword && (
        <ChangePasswordModal
          email={selectedUserForPassword.email}
          onClose={() => setSelectedUserForPassword(null)}
          onSubmit={handlePasswordUpdate}
        />
      )}
      {userToDelete && (
        <ConfirmDialog
          message={`Are you sure you want to permanently delete user "${userToDelete.email}"? This action cannot be undone.`}
          onYes={handleDeleteUser}
          onNo={() => setUserToDelete(null)}
          onCancel={() => setUserToDelete(null)}
        />
      )}
    </div>,
    document.body
  );
};

export default UserManagementModal;
